import { gql, useMutation, useQuery } from '@apollo/client'
import isEmpty from 'lodash/isEmpty'
import { useEffect } from 'react'

import {
  CustomProfileConnectionFragmentDoc,
  GenerateWallpaperPrintQrcodeMutation,
  GenerateWallpaperPrintQrcodeMutationVariables,
  ItemType,
  PortfolioConnectionFragment,
  PortfolioConnectionFragmentDoc,
  PortfolioQuery,
  PortfolioQueryVariables,
  PortfolioSkuFragmentDoc,
  PrepaidPointsPaymentSourceFragmentDoc,
  ProtectUserItemMutation,
  ProtectUserItemMutationVariables,
  PurchasablePrepaidPointConnectionFragmentDoc,
  PurchaseItemImmediately,
  PurchaseItemImmediatelyMutation,
  PurchaseItemImmediatelyMutationVariables,
  TicketStatusDetailFragmentDoc,
  UpdateCustomProfile,
  UpdateCustomProfileMutation,
  UpdateCustomProfileMutationVariables,
  UserItemSerialDetailFragment,
  UseTicketsMutation,
  UseTicketsMutationVariables,
  ViewCollectionItemMutation,
  ViewCollectionItemMutationVariables,
} from '@/clients/utoniq-core/schema'
import { useInitialLoading } from '@/components/lib/initialLoading'
import { usePointPurchase } from '@/components/pages/PrepaidPointCharge/viewModel'
import { config } from '@/config'
import { useCurrentUser, useMessage, useRouter, useTranslation } from '@/core'
import { ConversionCategories } from '@/core/analytics/ConversionKeys'
import { Share } from '@/core/native'
import { getPlatform } from '@/core/native/app'
import { useDeviceInfo } from '@/core/native/device'
import { convertPortfolioItemToSku, decodeSkuId } from '@/core/sku'
import { generateTransactionId, useAnalytics } from '@/lib/analytics'
import { useDownlodImage } from '@/lib/downloadFile'
import { useCreateShortLink } from '@/lib/url/shortLink'
import { createUuid } from '@/lib/uuid'

export type State = ReturnType<typeof useViewModel>['state']
export type Action = ReturnType<typeof useViewModel>['actions']

export const useViewModel = () => {
  const { loading: deviceInfoLoading } = useDeviceInfo()
  const { analytics } = useAnalytics()
  const { t: tcommon } = useTranslation()
  const { t } = useTranslation('ownedItemDetail')
  const { showError, showMessage } = useMessage()
  const { router } = useRouter<{
    itemId: string
    skuId?: string
  }>()
  const { user } = useCurrentUser()
  const platform = getPlatform()

  const {
    purchaseByIosAndAndroid,
    purchaseByWeb,
    completeNativePurchase,
    setCompleteNativePurchase,
    chargeLoading,
  } = usePointPurchase(user?.uid, platform, 'magazineViewer')

  const {
    data: portfolioData,
    refetch,
    loading,
  } = useQuery<PortfolioQuery, PortfolioQueryVariables>(PORTFOLIO, {
    variables: {
      id: router.query.itemId,
      platform,
      skuId: decodeSkuId(router.query.skuId ?? ''),
    },
    returnPartialData: true,
    skip: deviceInfoLoading,
    pollInterval: completeNativePurchase ? 2000 : 0,
  })

  const [updateCustomProfile, { loading: customProfileUpdating }] = useMutation<
    UpdateCustomProfileMutation,
    UpdateCustomProfileMutationVariables
  >(UpdateCustomProfile)

  const [protectUserItem, { loading: protectingItem }] = useMutation<
    ProtectUserItemMutation,
    ProtectUserItemMutationVariables
  >(PROTECT_USER_ITEM)

  const [onUseTickets] = useMutation<
    UseTicketsMutation,
    UseTicketsMutationVariables
  >(USE_TICKETS)

  const [viewCollectionItem] = useMutation<
    ViewCollectionItemMutation,
    ViewCollectionItemMutationVariables
  >(VIEW_COLLECTION_ITEM)

  // 決済後、prepaidPointBalanceが更新されたらpollingを停止する
  useEffect(() => {
    setCompleteNativePurchase(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioData?.prepaidPointBalance])

  // 閲覧履歴ミューテーションを送信する
  useEffect(() => {
    if (!portfolioData?.portfolio?.item?.collection?.isPublishOnStore) return
  }, [portfolioData?.portfolio?.item?.collection?.isPublishOnStore])

  const [purchaseItemImmediately, { loading: itemPurchasing }] = useMutation<
    PurchaseItemImmediatelyMutation,
    PurchaseItemImmediatelyMutationVariables
  >(PurchaseItemImmediately)
  const { downloadImage } = useDownlodImage()

  const [generateWallpaperPrintQrcode, { loading: QRCodePrinting }] =
    useMutation<
      GenerateWallpaperPrintQrcodeMutation,
      GenerateWallpaperPrintQrcodeMutationVariables
    >(GENERATE_WALLPAPER_PRINT_QRCODE)

  const getShortLinkUrl = useCreateShortLink({
    path: `/userProfile/${user?.uid}/ownedItems/${portfolioData?.portfolio?.item.id}`,
  })

  return {
    state: {
      loading: useInitialLoading(portfolioData, loading),
      QRCodePrinting: QRCodePrinting,
      loadingOnce: loading && !portfolioData?.portfolio,
      customProfileUpdating,
      portfolio: convertPortfolioItemToSku<PortfolioConnectionFragment | null>(
        (portfolioData?.portfolio as never) ?? null,
        router.query.skuId
      ),
      user: portfolioData?.user ?? null,
      loggedUser: user,
      isCardRegistered: !!portfolioData?.paymentSource?.last4,
      purchasablePrepaidPoints: portfolioData?.purchasablePrepaidPoints ?? null,
      point: portfolioData?.prepaidPointBalance?.balance ?? 0,
      chargeLoading,
      purchasingItem: itemPurchasing,
      protectingItem,
    },
    actions: {
      onClickShare: async () => {
        const url = await getShortLinkUrl()
        await Share.share({
          title: portfolioData?.portfolio?.item.title,
          text:
            portfolioData?.portfolio?.item.description ??
            portfolioData?.portfolio?.item.metadata?.ticket?.description ??
            tcommon('metaDescription'),
          url,
          dialogTitle: portfolioData?.portfolio?.item.title,
        }).catch(e => showError(e))

        return
      },
      onSettingAvatar: async () => {
        await updateCustomProfile({
          variables: {
            updateCustomProfileInputs: { avatarItemId: router.query.itemId },
          },
        })
        return
      },
      onSettingWallpaper: async () => {
        const unsetWallpaper =
          portfolioData?.user?.customProfile?.wallpaperItem?.id ===
          router.query.itemId
        await updateCustomProfile({
          variables: {
            updateCustomProfileInputs: {
              wallpaperItemId: unsetWallpaper ? null : router.query.itemId,
            },
          },
        })
        return !unsetWallpaper
      },
      async onProtectUserItem() {
        await protectUserItem({
          variables: {
            inputs: {
              isUserProtected: !portfolioData?.portfolio?.isUserProtected,
              id: portfolioData?.portfolio?.item.id ?? '',
            },
          },
        }).then(({ data }) => {
          if (data?.protectUserItem.isUserProtected) {
            showMessage(t('setProtectSuccess'))
            return
          }
          showMessage(t('unsetProtectSuccess'))
        })
      },
      /** マガジンの追加購入 */
      async onBuyItem(itemId: string, price: number, title: string) {
        analytics?.ecommerce({
          transaction_id: generateTransactionId(user?.uid),
          currency: 'JPY',
          type: 'convenience_store',
          items: [
            {
              item_id: itemId,
              item_name: title,
              item_category: ConversionCategories.storeItem,
              item_category2: ItemType.Magazine,
              item_category3: platform,
              item_category4: undefined,
              item_category5: portfolioData?.portfolio?.item?.issuedArtist?.id,
              price,
              quantity: 1,
            },
          ],
        })

        await purchaseItemImmediately({
          variables: {
            inputs: {
              itemId,
              price,
              count: 1,
            },
          },
          context: {
            headers: { 'x-utoniq-idempotency-key': createUuid() },
          },
        }).catch(error => {
          analytics?.track('error_cart_checkout', {
            applicationKey: config.applicationKey,
            artistId: portfolioData?.portfolio?.item?.issuedArtist?.id,
            error: error?.name,
            errorCode: error?.graphQLErrors?.[0]?.extensions?.errorCode,
          })

          if (
            error?.graphQLErrors?.[0]?.extensions?.errorCode === 'NEED_PATRON'
          ) {
            showError(new Error(t('needPatronErrorMessage')))
            return
          }

          showError(error)
        })
      },
      async onUseTicket(serialIds: string[], throughTicketName?: string) {
        await onUseTickets({
          variables: {
            inputs: {
              items: [
                { itemId: router.query.itemId, serialIds, throughTicketName },
              ],
            },
          },
        })
          .then(async () => {
            if (
              portfolioData?.portfolio?.item?.metadata?.ticket
                ?.distributionEnabled
            ) {
              await refetch()
            }
            showMessage(t('useTicketSuccess'))
          })
          .catch(showError)
      },
      // マガジンの閲覧履歴を送信する
      async onViewCollectionMagazine() {
        if (
          portfolioData?.portfolio?.item?.type !== ItemType.Magazine ||
          !portfolioData.portfolio?.item?.collection?.isPublishOnStore
        )
          return

        viewCollectionItem({
          variables: {
            inputs: {
              collectionId: portfolioData.portfolio?.item?.collection?.id ?? '',
              itemId: portfolioData.portfolio?.item?.id,
            },
          },
        })
      },
      // ポイントを購入する
      onPurchaseCharge:
        platform === 'web' ? purchaseByWeb : purchaseByIosAndAndroid,
      async onImageDownload(url: string | undefined) {
        if (isEmpty(url)) return
        url &&
          (await downloadImage(url).catch(e => {
            showError(e)
            throw e
          }))
      },
      async onGenerateWallpaperPrintQrcode(serialId: string) {
        await generateWallpaperPrintQrcode({
          variables: {
            inputs: { itemId: router.query.itemId, serialId },
          },
        })
      },
    },
  }
}

export function getSerialIdsByQuantity(
  serials: UserItemSerialDetailFragment[],
  quantity: number,
  reverse = false
): string[] {
  const serialsUnused = (reverse ? [...serials].reverse() : serials).filter(
    ({ serial }) => serial?.ticketStatus?.canUseTicket
  )
  return serialsUnused.slice(0, quantity).map(({ id }) => id)
}

const PORTFOLIO = gql`
  query portfolio(
    $id: ID!
    $platform: String!
    $pageInput: PageInput
    $skuId: String
  ) {
    portfolio(id: $id) {
      ...PortfolioSku
    }
    purchasablePrepaidPoints(platform: $platform, pageInput: $pageInput) {
      ...PurchasablePrepaidPointConnection
    }
    paymentSource {
      ...PrepaidPointsPaymentSource
    }
    prepaidPointBalance {
      prepaidPointId
      balance
    }
    user {
      id
      customProfile {
        ...CustomProfileConnection
      }
    }
  }
  ${PortfolioSkuFragmentDoc}
  ${CustomProfileConnectionFragmentDoc}
  ${PurchasablePrepaidPointConnectionFragmentDoc}
  ${PrepaidPointsPaymentSourceFragmentDoc}
`

const PROTECT_USER_ITEM = gql`
  mutation ProtectUserItem($inputs: ProtectUserItemInput!, $skuId: String) {
    protectUserItem(inputs: $inputs) {
      ...PortfolioConnection
    }
  }
  ${PortfolioConnectionFragmentDoc}
`

const USE_TICKETS = gql`
  mutation UseTickets($inputs: UseTicketsInput!) {
    useTickets(inputs: $inputs) {
      id
      obtainedDate
      ticketStatus {
        ...TicketStatusDetail
      }
    }
  }
  ${TicketStatusDetailFragmentDoc}
`

const VIEW_COLLECTION_ITEM = gql`
  mutation ViewCollectionItem($inputs: ViewCollectionItemInput!) {
    viewCollectionItem(inputs: $inputs) {
      id
      userId
      latestItemId
      collectionId
      viewCount
    }
  }
`

const GENERATE_WALLPAPER_PRINT_QRCODE = gql`
  mutation GenerateWallpaperPrintQrcode(
    $inputs: GenerateWallpaperPrintQrcodeInput!
  ) {
    generateWallpaperPrintQrcode(inputs: $inputs) {
      id
      ticketStatus {
        ...TicketStatusDetail
      }
      formattedAssignedNumber
      sku {
        id
        unitName
      }
      metadata {
        ticket {
          fullName
        }
        wallpaper {
          print {
            expiredAt
            contentsCode
            qrcode {
              uri
            }
          }
        }
      }
    }
  }
  ${TicketStatusDetailFragmentDoc}
`
